import { extendTheme, toThemeVars, defaultTheme } from "@mozioinc/ui/server";
import { mix, toHsla } from "color2k";
import merge from "lodash.merge";
import type {
  WhiteLabelInjectData,
  WhiteLabelDomainData,
} from "@mozioinc/query";

export type ParkingWhiteLabelInjectData = WhiteLabelInjectData & {
  config: WhiteLabelInjectData["config"] & {
    header: { logo: string; showESim: boolean; showTransfers: boolean };
    banners: {
      showTransfers: boolean;
      showESim: boolean;
      showLounges: boolean;
    };
    footer: {
      logo: string;
      phoneNumber: { default: string } & Record<string, string>; // the key is either default or a locale
      emailLink: string;
      socials?: {
        facebook?: string;
        twitter?: string;
        instagram?: string;
        linkedin?: string;
      };
      navLinks?: {
        href: string;
        content: string;
      }[];
      termsAndPrivacyLinks?: {
        href: string;
        content: string;
      }[];
    };
    heroImageUrl?: string;
  };
};

export const defaultWhiteLabelData = {
  metadata: {
    title: "Mozio",
    description: "",
    keywords:
      "taxi service, yellow cab, cab, chauffeur, super shuttle, limo, Airport Transfer, Express Train, Ground Transport, ground transportation, Transfer, Transfers, Holiday Taxis, airport transfers, airport taxi, taxis, taxi, transfer, transfers, taxi transfers, hotel transfers, minibus, coach, cities, airport, limousine, shuttles, esim, sim, parking",
    imageUrl: "https://static.mozio.com/assets/logo-og.png",
    faviconUrl: "https://static.mozio.com/assets/favicon.ico",
    canonicalUrl: "https://parking.mozio.com",
    url: "https://parking.mozio.com",
  },
  themeVars: toThemeVars(defaultTheme),
  config: {
    name: "mozio",
    partnerRef: "mozio",
    domain: "parking.mozio.com",
    locale: "en_US",
    currency: "USD",
    authRequired: false,
    header: {
      logo: "https://static.mozio.com/assets/logo.svg",
      showESim: true,
      showTransfers: true,
    },
    banners: {
      showTransfers: true,
      showESim: true,
      showLounges: true,
    },
    footer: {
      logo: "https://static.mozio.com/assets/logo.svg",
      phoneNumber: {
        default: "+1 (855) 980-7116",
      },
      emailLink: "travel-extras@mozio.com",
    },
  },
} satisfies ParkingWhiteLabelInjectData;

export const mapWhiteLabelInjectData = (
  data: WhiteLabelDomainData,
): ParkingWhiteLabelInjectData => {
  const { domain, locale, currency, config } = data;

  const whiteLabelTheme = extendTheme({
    palette: {
      primary: toThemeColors(
        config.FIRST_BACKGROUND_COLOR,
        config.FIRST_CONTRAST_COLOR,
      ),
      secondary: toThemeColors(
        config.FOURTH_BACKGROUND_COLOR,
        config.FOURTH_CONTRAST_COLOR,
      ),
    },
    header: {
      logoHeight: config.HEADER_CUSTOM_LOGO_HEIGHT || undefined,
    },
  });

  return merge({}, defaultWhiteLabelData, {
    metadata: {
      imageUrl:
        config.CUSTOM_SOCIAL_LOGO ||
        config.CUSTOM_LOGO ||
        "https://static.mozio.com/assets/logo-og.png",
      faviconUrl: config.CUSTOM_FAVICON,
    },
    themeVars: toThemeVars(whiteLabelTheme),
    config: {
      id: config.ID,
      name: config.NAME,
      domain,
      locale,
      currency,
      partnerRef: config.PARTNER_REF,
      heroImageUrl: config.PARKING_COVER_IMAGE,
      header: {
        logo: config.CUSTOM_LOGO || undefined,
        showESim: config.PARKING_SHOW_HEADER_ESIM,
        showTransfers: config.PARKING_SHOW_HEADER_TRANSFER,
      },
      banners: {
        showTransfers: config.PARKING_SHOW_TRANSFER_BANNER,
        showESim: config.PARKING_SHOW_ESIM_BANNER,
        showLounges: config.PARKING_SHOW_LOUNGES_BANNER,
      },
      footer: {
        logo: config.CUSTOM_FOOTER_LOGO || undefined,
        socials: Object.fromEntries(config.FOOTER_SOCIALS),
        phoneNumber: config.ESIM_SUPPORT_PHONE_NUMBERS
          ? Object.fromEntries(config.ESIM_SUPPORT_PHONE_NUMBERS)
          : undefined,
        emailLink: config.ESIM_SUPPORT_EMAIL,
      },
    },
  });
};

const toHsl = (hsla: string) => {
  return hsla.replace("hsla", "hsl").replace(", 1)", ")").replaceAll(",", "");
};

const toThemeColors = (color: string, contrastColor: string) => {
  return {
    100: toHsl(toHsla(mix(color, "#FFFFFF", 0.8))),
    200: toHsl(toHsla(mix(color, "#FFFFFF", 0.3))),
    300: toHsl(toHsla(color)),
    400: toHsl(toHsla(mix(color, "#000000", 0.3))),
    500: toHsl(toHsla(mix(color, "#000000", 0.6))),
    contrast: toHsl(toHsla(contrastColor)),
  };
};
